import type { IconButtonProps } from './index.types';
import * as React from 'react';
import { Button } from '~/lib/ui/button';
import { cn } from '~/lib/utils';

/**
 * IconButton - A reusable icon button component with label
 *
 * Displays a circular button with an SVG icon and a label underneath.
 * Commonly used for action buttons in toolbars and control panels.
 */
export const IconButton = React.forwardRef<HTMLDivElement, IconButtonProps>(
  (
    {
      icon,
      label,
      onClick,
      onFocus,
      variant = 'outline',
      disabled = false,
      iconSize = 24,
      containerClassName = '',
      buttonClassName = '',
      iconClassName = '',
      labelClassName = '',
      isPressed,
      testID,
      title,
      ariaLabel,
      keepFocusableWhenDisabled = false,
      busy = false,
      ariaDescribedBy,
    },
    ref
  ) => {
    // Mutually exclusive: the native attribute drops the control from the tab
    // order, `aria-disabled` keeps it reachable. Same styling either way.
    const isSoftDisabled = disabled && keepFocusableWhenDisabled;
    const isNativelyDisabled = disabled && !keepFocusableWhenDisabled;

    // Busy blocks activation and dims like disabled, minus `aria-disabled`.
    const isInert = isSoftDisabled || busy;

    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col items-center gap-[4px]',
          containerClassName
        )}
        data-testid={testID}
        title={title}
      >
        <Button
          variant={variant}
          size="icon"
          // Blocks the keyboard; `pointer-events-none` below blocks the mouse.
          onClick={isInert ? undefined : onClick}
          onFocus={onFocus}
          disabled={isNativelyDisabled}
          className={cn(
            'rounded-full box-border size-[40px]',
            // What the `disabled:` variants apply, by hand.
            isInert && 'opacity-50 pointer-events-none',
            buttonClassName
          )}
          aria-label={ariaLabel ?? label}
          aria-describedby={ariaDescribedBy}
          {...(isSoftDisabled && { 'aria-disabled': true })}
          {...(busy && { 'aria-busy': true })}
          {...(isPressed !== undefined && { 'aria-pressed': isPressed })}
        >
          {React.createElement(icon, {
            width: iconSize,
            height: iconSize,
            style: { width: `${iconSize}px`, height: `${iconSize}px` },
            className: iconClassName,
            'aria-hidden': true,
            focusable: false,
          })}
        </Button>

        {label && (
          // The name is `ariaLabel ?? label`, so this caption only repeats it.
          <span
            aria-hidden="true"
            className={cn(
              'text-[14px]',
              isInert || disabled ? 'text-muted-foreground' : 'text-foreground',
              labelClassName
            )}
          >
            {label}
          </span>
        )}
      </div>
    );
  }
);

IconButton.displayName = 'IconButton';
